import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Trash2, ArrowRight, Minus, Plus, Ruler } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { customer, patchCustomer } = useApp();
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!customer?.cart?.length) { setItems([]); return; }
    Promise.all(customer.cart.map(c =>
      api.get(`/products/${c.product_id}`).then(r => ({
        ...r.data,
        boxes: c.boxes,
        length: c.length ?? 0,
        width_or_height: c.width_or_height ?? c.width ?? 0,
      }))
    )).then(setItems);
  }, [customer]);

  const updateEntry = async (product_id, patch) => {
    const cart = customer.cart.map(c =>
      c.product_id === product_id ? { ...c, ...patch } : c
    );
    await patchCustomer({ cart });
  };

  const updateBoxes = (product_id, delta, current) => {
    updateEntry(product_id, { boxes: Math.max(1, current + delta) });
  };

  const setBoxesDirect = (product_id, value) => {
    const n = Math.max(1, parseInt(value) || 1);
    updateEntry(product_id, { boxes: n });
  };

  const recalcFromDims = async (item, length, width) => {
    const L = parseFloat(length), W = parseFloat(width);
    if (!L || !W) {
      await updateEntry(item.id, { length: L || 0, width_or_height: W || 0 });
      return;
    }
    try {
      const { data } = await api.post("/calc/room", {
        length: L, width: W, tile_area_sqft: item.tile_area_sqft, packing: item.packing,
      });
      await updateEntry(item.id, {
        length: L,
        width_or_height: W,
        boxes: data.boxes_required,
      });
      toast.success(`${item.our_code}: ${data.boxes_required} boxes`);
    } catch (e) {
      toast.error("Could not recalculate");
    }
  };

  const remove = async (product_id) => {
    const cart = customer.cart.filter(c => c.product_id !== product_id);
    await patchCustomer({ cart });
  };

  if (!customer) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="cart-empty">
        <p className="label-eyebrow">Your cart</p>
        <h2 className="font-editorial text-4xl mt-3">Sign in to see your cart</h2>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-24 text-center" data-testid="cart-empty">
        <p className="label-eyebrow">Your cart</p>
        <h2 className="font-editorial text-4xl mt-3">The cart is empty</h2>
        <Link to="/catalog" className="btn-clay mt-8 inline-flex" data-testid="cart-shop">Continue Shopping</Link>
      </div>
    );
  }

  const subtotal = items.reduce((s, it) => s + Number(it.offer_price || 0) * Number(it.boxes || 0), 0);
  const tax = 0; // GST is already INCLUDED in offer_price — do not add on top
  const total = subtotal;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="cart-page">
      <p className="label-eyebrow">Your Selection</p>
      <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3 mb-12">Cart</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 divide-y divide-[#1A1A1A]/10">
          {items.map(it => {
            const isWall = (it.tile_type || "floor") === "wall";
            return (
              <div key={it.id} className="py-8" data-testid={`cart-item-${it.our_code}`}>
                <div className="grid grid-cols-12 gap-6 items-start">
                  <div className="col-span-3 aspect-square bg-[#F0EFEA]">
                    <img src={it.image_url} alt={it.our_code} className="w-full h-full object-cover" />
                  </div>
                  <div className="col-span-9">
                    <p className="label-eyebrow">
                      {it.category} · {it.size} · <span className="text-[#1E2C5A]">{(it.tile_type || "floor").toUpperCase()}</span>
                    </p>
                    <h3 className="font-editorial text-2xl mt-1">{it.our_code}</h3>
                    <p className="text-xs text-[#8A8A8A] mt-1">{it.packing} tiles/box · ₹{it.offer_price}/{(it.primary_uom || "BOX").toLowerCase()} (GST incl.)</p>

                    {/* Editable dimensions */}
                    <div className="mt-4 flex flex-wrap gap-4 items-end">
                      <div className="flex items-center gap-2">
                        <Ruler className="w-3.5 h-3.5 text-[#1E2C5A]" strokeWidth={1.5} />
                        <span className="label-eyebrow">{isWall ? "L × H" : "L × W"} (ft)</span>
                      </div>
                      <input
                        type="number"
                        defaultValue={it.length || ""}
                        placeholder="Length"
                        className="input-editorial !py-1 !text-sm max-w-[90px]"
                        onBlur={(e) => recalcFromDims(it, e.target.value, it.width_or_height)}
                        data-testid={`cart-length-${it.our_code}`}
                      />
                      <span className="text-[#8A8A8A]">×</span>
                      <input
                        type="number"
                        defaultValue={it.width_or_height || ""}
                        placeholder={isWall ? "Height" : "Width"}
                        className="input-editorial !py-1 !text-sm max-w-[90px]"
                        onBlur={(e) => recalcFromDims(it, it.length, e.target.value)}
                        data-testid={`cart-width-${it.our_code}`}
                      />
                      {it.length > 0 && it.width_or_height > 0 && (
                        <span className="text-xs text-[#5C5C5C]">
                          = {(it.length * it.width_or_height).toFixed(1)} sqft
                        </span>
                      )}
                    </div>

                    {/* Boxes editable */}
                    <div className="mt-5 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <span className="label-eyebrow">Boxes</span>
                        <button onClick={() => updateBoxes(it.id, -1, it.boxes)} className="border border-[#1A1A1A]/30 w-8 h-8 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors" data-testid={`cart-dec-${it.our_code}`}><Minus className="w-3 h-3" strokeWidth={1.5} /></button>
                        <input
                          type="number"
                          min="1"
                          value={it.boxes}
                          onChange={(e) => setBoxesDirect(it.id, e.target.value)}
                          className="w-14 text-center border border-[#1A1A1A]/30 h-8 text-sm"
                          data-testid={`cart-qty-${it.our_code}`}
                        />
                        <button onClick={() => updateBoxes(it.id, +1, it.boxes)} className="border border-[#1A1A1A]/30 w-8 h-8 flex items-center justify-center hover:bg-[#1A1A1A] hover:text-white transition-colors" data-testid={`cart-inc-${it.our_code}`}><Plus className="w-3 h-3" strokeWidth={1.5} /></button>
                      </div>
                      <div className="text-right">
                        <p className="font-editorial text-2xl">₹{(Number(it.offer_price || 0) * Number(it.boxes || 0)).toFixed(0)}</p>
                        <button onClick={() => remove(it.id)} className="text-xs text-[#8A8A8A] hover:text-[#1E2C5A] transition-colors mt-1 inline-flex items-center gap-1" data-testid={`cart-remove-${it.our_code}`}>
                          <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-[#F0EFEA] p-10 h-fit sticky top-32" data-testid="cart-summary">
          <p className="label-eyebrow">Summary</p>
          <h3 className="font-editorial text-3xl mt-2 mb-8">Order Total</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#5C5C5C]">Subtotal <span className="text-[10px] text-[#8A8A8A]">(GST incl.)</span></span><span data-testid="cart-subtotal">₹{subtotal.toFixed(0)}</span></div>
          </div>
          <div className="border-t border-[#1A1A1A]/20 mt-6 pt-6 flex justify-between items-baseline">
            <span className="label-eyebrow !text-[#1A1A1A]">Total</span>
            <span className="font-editorial text-4xl text-[#1E2C5A]" data-testid="cart-total">₹{total.toFixed(0)}</span>
          </div>
          <button onClick={() => nav("/checkout")} className="btn-clay w-full mt-8" data-testid="cart-checkout">
            Proceed to Checkout <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
